/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useEffect, useState } from 'react'
import { View, BoxView, ActionButton } from '@components/atoms'
import { ProductList } from '@components/organisms/product/productList'
import { useAppDispatch, useAppSelector } from '@redux/index'
import { productActions } from '@redux/store/index'
import { ProductType, BomType, BomAddType, BomUpdateType } from '@schemas'
import { ProductRegistrationModal } from '@components/molecules'
import { BOMList } from '@components/organisms'
import { mono, boShared, boMedia } from '@components/systems/backoffice/backoffice.tokens'
import { BoSplit } from '@components/systems/backoffice/BoSplit'

const ProductPage = () => {
	const reduxDispatch = useAppDispatch()
	const { products, selectedProduct, boms, loading } = useAppSelector(
		state => state.product,
	)

	const [modalOpen, setModalOpen] = useState(false)
	const [isEditMode, setIsEditMode] = useState(false)
	const [currentBOMs, setCurrentBOMs] = useState<BomType[]>([])

	useEffect(() => {
		if (boms) {
			setCurrentBOMs(JSON.parse(JSON.stringify(boms)))
		}
	}, [boms])

	const handleProductClick = (product: ProductType) => {
		reduxDispatch(productActions.SET_SELECTED_PRODUCT(product))
		reduxDispatch(productActions.FETCH_BOM(product.id))
	}

	const handleOpenModal = () => {
		setModalOpen(true)
	}

	const handleCloseModal = () => {
		setModalOpen(false)
	}

	const handleEditMode = () => {
		if (isEditMode) {
			setIsEditMode(false)
			setCurrentBOMs(JSON.parse(JSON.stringify(boms)))
		} else {
			setIsEditMode(true)
		}
	}

	const handleUpdateBOM = (updatedBOM: BomType[], deletedBOMs: BomType[]) => {
		const batchUpdatePayload = updatedBOM.map(bom => {
			if (bom.id.startsWith('new-')) {
				return {
					iudFlag: 'I',
					productId: selectedProduct?.id,
					manufacturer: bom.manufacturer,
					name: bom.name,
					jlcpcbNumber: bom.jlcpcbNumber,
					link: bom.link,
					quantity: bom.quantity,
					unitUsd: bom.unitUsd,
					useYn: String(bom.useYn),
				} as BomAddType
			} else {
				return {
					iudFlag: 'U',
					id: bom.id,
					productId: selectedProduct?.id,
					manufacturer: bom.manufacturer,
					name: bom.name,
					jlcpcbNumber: bom.jlcpcbNumber,
					link: bom.link,
					quantity: bom.quantity,
					unitUsd: bom.unitUsd,
					useYn: String(bom.useYn),
				} as BomUpdateType
			}
		})

		const batchDeletePayload = deletedBOMs
			.filter(bom => !bom.id.startsWith('new-'))
			.map(bom => ({
				iudFlag: 'D' as const,
				id: bom.id,
			}))

		reduxDispatch(
			productActions.BATCH_UPDATE_BOM([...batchUpdatePayload, ...batchDeletePayload]),
		)
	}

	return (
		<View css={style}>
			<BoSplit
				className="productOuterView"
				title="BOM"
				detailKey={selectedProduct ? String(selectedProduct.id) : ''}
				list={
					<BoxView
						title="제품 리스트"
						className="productView"
						rightHeaderAccessory={
							<ActionButton
								title="제품 등록"
								iconName="plus"
								buttonColor="primary"
								variant="contained"
								onClick={handleOpenModal}
							/>
						}
					>
						<View className="listOuterView">
							{loading ? (
								<p className="boEmpty">Loading...</p>
							) : (
								<ProductList
									products={products}
									onProductClick={handleProductClick}
									selectedProductId={selectedProduct?.id}
								/>
							)}
						</View>
					</BoxView>
				}
				detail={
					<BoxView
						title={`BOM 리스트 ${
							selectedProduct ? `- ${selectedProduct.name as string}` : ''
						}`}
						className="bomView"
						rightHeaderAccessory={
							selectedProduct && (
								<ActionButton
									title={isEditMode ? '수정 종료' : '수정'}
									iconName={isEditMode ? 'pencil-off-outline' : 'pencil-outline'}
									variant="outlined"
									onClick={handleEditMode}
								/>
							)
						}
					>
						{selectedProduct ? (
							<BOMList
								boms={isEditMode ? currentBOMs : boms}
								isEditMode={isEditMode}
								onUpdateBOM={handleUpdateBOM}
							/>
						) : (
							<p className="boEmpty">Select a product to view its BOM</p>
						)}
					</BoxView>
				}
			/>
			<ProductRegistrationModal open={modalOpen} onClose={handleCloseModal} />
		</View>
	)
}

export default ProductPage

const style = css({
	...boShared,
	height: '100%',

	'.productOuterView': {
		position: 'relative',
		display: 'flex',
		height: '100%',

		'.listOuterView': {
			width: '100%',
			height: '100%',
			border: 0,
			borderRadius: 0,
			backgroundColor: 'transparent',
			overflowY: 'auto',
		},

		'.productView': {
			width: '30rem',
			paddingRight: 24,
			borderRight: `1px solid ${mono.line}`,
		},
		'.bomView': {
			width: 'calc(100% - 30rem)',
		},

		[boMedia.notDesktop]: {
			flexDirection: 'column',
			'.productView': {
				width: '100%',
				paddingRight: 0,
				borderRight: 0,
				borderBottom: `1px solid ${mono.line}`,
				marginBottom: 16,
			},
			'.bomView': { width: '100%' },
		},
	},
})
